import React, { useState, useCallback } from 'react';
import { Text, RadioButton, TwoLineDivider, Button } from '@moda/om';
import { ShippingAddressInfoBlock } from '../ShippingAddressInfoBlock/ShippingAddressInfoBlock';
import { useValidateAddressQuery, RestAddressAttributesData } from '../../../hooks/useAddress';
import { ShippingAddressLoading } from '../ShippingAddressLoading';
import { ShippingAddressModal } from '../ShippingAddressModal/ShippingAddressModal';

enum AddressOption {
  AddressEntered,
  VerifiedMatch
}

interface Props {
  address: RestAddressAttributesData;
  onSubmit: (address: RestAddressAttributesData) => void;
  onPopupClose: () => void;
}

export const ShippingAddressValidationPopup: React.FC<Props> = ({
  address,
  onSubmit,
  onPopupClose
}) => {
  const [selectedOption, setSelectedOption] = useState<AddressOption | undefined>(undefined);
  const { address: verifiedMatch, loading } = useValidateAddressQuery(address);

  const handleSubmit = useCallback(() => {
    switch (selectedOption) {
      case AddressOption.AddressEntered: {
        onSubmit(address);
        break;
      }
      case AddressOption.VerifiedMatch: {
        if (verifiedMatch) {
          onSubmit(verifiedMatch);
        }
        break;
      }
      default:
        return;
    }
  }, [address, onSubmit, selectedOption, verifiedMatch]);

  return loading ? (
    <ShippingAddressLoading retrieving />
  ) : (
    <ShippingAddressModal onClose={onPopupClose}>
      <Text family="serif" treatment="h4">
        Please confirm this postal address
      </Text>
      <TwoLineDivider text="Address entered" />
      <RadioButton
        checked={selectedOption === AddressOption.AddressEntered}
        onChange={() => setSelectedOption(AddressOption.AddressEntered)}
        type="radio"
      >
        <ShippingAddressInfoBlock data={address} />
      </RadioButton>
      {verifiedMatch && (
        <>
          <TwoLineDivider text="Verified Match" />
          <RadioButton
            checked={selectedOption === AddressOption.VerifiedMatch}
            onChange={() => setSelectedOption(AddressOption.VerifiedMatch)}
            type="radio"
          >
            <ShippingAddressInfoBlock data={verifiedMatch} />
          </RadioButton>
        </>
      )}
      <Button disabled={selectedOption === undefined} onClick={handleSubmit} type="button">
        Confirm Address
      </Button>
    </ShippingAddressModal>
  );
};
